import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { appendTrackingParams } from '../../utils/utm';

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const { pathname } = useLocation();
  const onLanding = pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const effectiveScrolled = scrolled || !onLanding;

  useEffect(() => {
    if (!onLanding) return undefined;
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight - 80);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onLanding]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => setOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          effectiveScrolled || open ? 'bg-black/45 backdrop-blur-md' : 'bg-transparent'        }`}
        data-testid="navbar">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-logo" aria-label="evern" className="block" onClick={close}>
            <img
              src="/assets/evern-logo.png"
              alt="evern"
              className={`h-5 md:h-6 w-auto transition-opacity duration-300 ${effectiveScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            {onLanding && (
              <>
                <a href="#features" className="font-body text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">{t.nav_features}</a>
                <a href="#pricing" className="font-body text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">{t.nav_pricing}</a>
                <a href="#faq" className="font-body text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">{t.nav_faqs}</a>
              </>
            )}
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em]">
              <button onClick={() => setLang('en')} className={`${lang === 'en' ? 'text-white' : 'text-white/50'} hover:text-white transition-colors`} data-testid="lang-en">EN</button>
              <span className="text-white/30">/</span>
              <button onClick={() => setLang('es')} className={`${lang === 'es' ? 'text-white' : 'text-white/50'} hover:text-white transition-colors`} data-testid="lang-es">ES</button>
            </div>
            <a
              href={appendTrackingParams(t.hero_cta_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-5 py-2 bg-ev-lime hover:bg-ev-lime-dim text-ev-ink font-body font-medium text-xs uppercase tracking-[0.15em] rounded-full transition-colors"
              data-testid="nav-cta">
              {t.nav_cta || t.hero_cta}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="md:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-[5px]"
            data-testid="mobile-menu-toggle">
            <span className={`block h-[2px] w-6 bg-white transition-transform duration-300 ${open ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block h-[2px] w-6 bg-white transition-opacity duration-300 ${open ? 'opacity-0' : 'opacity-100'}`} />
            <span className={`block h-[2px] w-6 bg-white transition-transform duration-300 ${open ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-drawer">
        <div
          className="absolute inset-0 bg-black/90 backdrop-blur-lg"
          onClick={close}
          aria-hidden="true"
        />
        <div className="relative h-full flex flex-col items-center justify-center px-8 gap-10 text-center">
          {onLanding && (
            <div className="flex flex-col items-center gap-7">
              <a href="#features" onClick={close} className="font-display text-3xl text-white lowercase hover:text-ev-lime transition-colors" data-testid="m-nav-features">{t.nav_features}</a>
              <a href="#pricing" onClick={close} className="font-display text-3xl text-white lowercase hover:text-ev-lime transition-colors" data-testid="m-nav-pricing">{t.nav_pricing}</a>
              <a href="#faq" onClick={close} className="font-display text-3xl text-white lowercase hover:text-ev-lime transition-colors" data-testid="m-nav-faqs">{t.nav_faqs}</a>
            </div>
          )}
          <div className="flex items-center gap-3 font-body text-sm uppercase tracking-[0.2em]">
            <button onClick={() => { setLang('en'); }} className={`${lang === 'en' ? 'text-white' : 'text-white/50'}`} data-testid="m-lang-en">EN</button>
            <span className="text-white/30">/</span>
            <button onClick={() => { setLang('es'); }} className={`${lang === 'es' ? 'text-white' : 'text-white/50'}`} data-testid="m-lang-es">ES</button>
          </div>
          <a
            href={appendTrackingParams(t.hero_cta_url)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className="inline-flex items-center px-8 py-3.5 bg-ev-lime hover:bg-ev-lime-dim text-ev-ink font-body font-medium text-sm uppercase tracking-[0.15em] rounded-full transition-colors"
            data-testid="m-nav-cta">
            {t.nav_cta || t.hero_cta}
          </a>
        </div>
      </div>
    </>
  );
}
